import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewEncapsulation
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Utils } from 'formiojs';
import _cloneDeep from 'lodash/cloneDeep';
import * as moment from 'moment';
import {
	BehaviorSubject,
	Subject,
	distinctUntilChanged,
	finalize,
	map,
	of,
	takeUntil,
	withLatestFrom
} from 'rxjs';
import { FormioCustomComponent, FormioEvent, FormioLoader, ResourceNameService } from 'dp-formio';
import { ComponentMode, VisitScheduleOptions } from './models/options';
@Component({
	selector: 'app-visit-schedule',
	templateUrl: './visit-schedule.component.html',
	styleUrls: ['./visit-schedule.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [FormioLoader],
})
export class VisitScheduleComponent
	implements FormioCustomComponent<any>, OnInit, OnChanges, OnDestroy {
	@Input() data: any;
	@Input() customOptions: Partial<VisitScheduleOptions>;
	@Input() formioComponent: any;
	@Input() required: boolean;
	@Input() disabled: boolean;
	@Input() viewOnly: boolean;
	@Input() value: {
		events?: any[];
	} = {};

	@Output() valueChange = new EventEmitter<any>();

	formioEvent?: EventEmitter<FormioEvent>;

	destroy$ = new Subject<void>();

	private _defaultEvents = {};
	private _events = [];
	private _store = new Map();
	private _eventsSub = new BehaviorSubject(this.value);
	private _visitName = '';
	private _scheduleNameSub = new Subject();

	events$ = this._eventsSub.asObservable();
	scheduleName$ = this._scheduleNameSub.asObservable().pipe(distinctUntilChanged());
	isLoadingResults = false;

	constructor(
		private translateService: TranslateService,
		private resourceNameService: ResourceNameService
	) { }

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.data?.currentValue) {
			this._visitName = changes.data.currentValue?.visitScheduleName?.data?.value;
		}
		if (
			changes?.customOptions?.currentValue?.mode === ComponentMode.CREATE &&
			changes?.data?.currentValue &&
			!this.value
		) {
			this._prepareData();
		}

		if (
			this.value &&
			changes?.customOptions?.currentValue?.mode === ComponentMode.CREATE &&
			changes?.data?.currentValue
		) {
			this._mergeValuesWithDefaults();
		}
		if (
			changes?.data?.currentValue &&
			changes?.customOptions?.currentValue?.mode === ComponentMode.EDIT
		) {
			this._updateValues();
		}
	}

	ngOnInit() {
		this._listenerToScheduleName();
		this.scheduleName$.pipe(takeUntil(this.destroy$)).subscribe((name: string) => {
			this._visitName = name;
			if (this.customOptions.mode === ComponentMode.CREATE) {
				if (!this.value) {
					this._prepareData();
				} else {
					if (this._store.has(name)) {
						this._updateEventsAndEmitValue(this._store.get(name));
					} else {
						this._mergeValuesWithDefaults();
					}
				}
			}
			if (this.customOptions.mode === ComponentMode.EDIT) {
				if (this._store.has(name)) {
					this._updateEventsAndEmitValue(this._store.get(name));
				} else {
					this._prepareEditData();
				}
			}
		});
	}
	private _prepareEditData() {
		const data = this._generateDefaultEvents() ?? {};
		const obj = { events: Object.values(data) };
		this._updateValues(obj);
		this.notifyValue(obj);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	calendarValueChanged(event) {
		this._store.set(this._visitName, event);
		this._events = event?.events;
		this._updateEventsAndEmitValue(event);
	}

	notifyValue(value: any) {
		this.value = _cloneDeep(value);
		this.valueChange.emit(this.value);
	}

	private _updateValues(value = this.value) {
		if (value) {
			this._events = value?.events;
			this._eventsSub.next({ events: this._events });
			if (!this._store.has(this._visitName)) {
				this._store.set(this._visitName, { events: this._events });
			}
		}
	}

	private _updateEventsAndEmitValue(event) {
		this._eventsSub.next(event);
		this.notifyValue(event);
	}

	private _listenerToScheduleName() {
		const listenerName = this.customOptions?.listener ?? 'visitScheduleName.changed';
		this.formioComponent?.events?.on(listenerName, (data) => this._scheduleNameSub.next(data));
	}

	private _mergeValuesWithDefaults() {
		if (this.data?.visitScheduleName) {
			this.isLoadingResults = true;
			of(this._generateDefaultEvents())
				.pipe(
					takeUntil(this.destroy$),
					map((res) => {
						const value = this.value?.events.filter(
							(itm) => itm.data.data.defaultVisit == this._visitName
						);
						const valueObj = this._convertToObjById(value);
						const result = Object.values({
							...res,
							...valueObj,
						});
						return result;
					}),
					finalize(() => {
						this.isLoadingResults = false;
					})
				)
				.subscribe((data) => {
					this._events = data;
					this._eventsSub.next({ events: this._events });
					if (!this._store.has(this._visitName)) {
						this._store.set(this._visitName, { events: this._events });
					}
					this.notifyValue({ events: this._events });
				});
		}
	}

	private _convertToObjById(data: any[]): any {
		return data.reduce((acc, itm) => {
			return {
				...acc,
				[itm['id']]: itm,
			};
		}, {});
	}

	private _prepareData() {
		if (this.data?.visitScheduleName) {
			this.isLoadingResults = true;
			this._getDataFromResource()
				.pipe(
					takeUntil(this.destroy$),
					map((events: any[]) => this._convertToResourceObj(events.map((event) => event?.data))),
					withLatestFrom(of(this._generateDefaultEvents())),
					map(([resData, defData]) =>
						Object.values({
							...defData,
							...resData,
						})
					),
					finalize(() => {
						this.isLoadingResults = false;
					})
				)
				.subscribe((data) => {
					this._events = data;
					this._eventsSub.next({ events: this._events });
					this.notifyValue({ events: this._events });
				});
		}
	}

	private _convertToResourceObj(source: any[]) {
		return source.reduce((acc, item) => {
			return {
				...acc,
				[item['sessionId']]: {
					id: item?.sessionId,
					start: item?.start,
					end: item?.end,
					title: item?.title,
					data: {
						Submit: item?.Submit,
						data: item,
					},
				},
			};
		}, {});
	}

	private _getDataFromResource() {
		const resource = this.customOptions.resource;
		const key = resource.key ?? 'visitsession';
		let query = `data.visitKey=${this.data?.requestDetails?.data.projectKey}&data.defaultVisit=${this.data?.visitScheduleName?.data?.value}`;
		if (resource?.query) {
			query = Utils.evaluate(resource?.query, { data: this.data });
		}
		return this.resourceNameService.loadCustomByFilterdKey(key, query);
	}

	private _generateDefaultEvents(events = this._defaultEvents) {
		let { visitEndDate, visitStartDate, visitStyle, varRequestId } =
			this.data?.requestDetails?.data;

		const defaultVisits = this.data?.visitScheduleName?.data?.sessions?.events;

		const defaultVisit = this.data?.visitScheduleName?.data?.value;

		var visitDays = moment(visitEndDate).diff(moment(visitStartDate), 'days');

		if (defaultVisits?.length && visitStartDate && visitEndDate) {
			defaultVisits.forEach((day, index) => {
				if (index <= visitDays && day?.length) {
					const startDay = moment(visitStartDate).add(index, 'day').format('DD-MM-YYYY').toString();

					if (day?.length) {
						day.forEach((session, idx) => {
							const start = moment(startDay, 'DD-MM-YYYY').add(session?.from).format().toString();
							const end = moment(startDay, 'DD-MM-YYYY').add(session?.to).format().toString();

							const _id = this._generateDefaultsId(
								index,
								idx,
								Utils.guid(),
								session?.sessionType,
								varRequestId,
								defaultVisit
							);

							events[_id] = {
								id: _id,
								start: start,
								end: end,
								title: session?.sessionType,
								color:
									this.customOptions?.colors?.invalid ??
									'var(--invalid-color, red)',
								data: {
									Submit: false,
									data: {
										...session,
										defaultVisit: defaultVisit ?? '',
										sessionId: _id,
										Submit: false,
										sessionType: session?.sessionType ?? visitStyle,
										start: start,
										end: end,
										dateRange: this._getVisitRange(
											visitStartDate,
											visitEndDate
										),
									},
								},
							};
						});
					}
				}
			});
		}
		return events;
	}

	private _generateDefaultsId(
		day: number,
		sessionIndex: number,
		sessionNumber: number,
		sessionType: any,
		varRequestId: string,
		defaultVisit: string
	) {
		return `${day}_${sessionIndex}_${sessionNumber}_${defaultVisit}_${varRequestId}_${sessionType}`;
	}

	private _getVisitRange(start, end) {
		return moment(start).format('DD/MM/YYYY') + '  -  ' + moment(end).format('DD/MM/YYYY');
	}
}
